import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LiveTickerTape } from './TickerTape';
import { formatCurrency, formatPercent } from '../utils/formatters';
import '../styles/HomePage.css';
import '../styles/trading-terminal.css';
import { API_BASE_URL } from '../config';
import io from 'socket.io-client';

const HomePage = () => {
  const [marketData, setMarketData] = useState({
    stocks: [],
    marketStats: null,
    topMovers: [],
    mostActive: []
  });
  const [marketStatus, setMarketStatus] = useState('LOADING');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marketEvents, setMarketEvents] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Countdown states
  const [premarketCountdown, setPremarketCountdown] = useState(null); // Long countdown to market open (~60 min)
  const [marketOpenCountdown, setMarketOpenCountdown] = useState(null); // Short countdown to market open (HARD)
  const [macroGameCountdown, setMacroGameCountdown] = useState(null); // Countdown to next macro game (HARD)
  const [tradingBlockCountdown, setTradingBlockCountdown] = useState(null); // Trading session countdown (SOFT)
  const [closeCountdown, setCloseCountdown] = useState(null); // Closing bell countdown (HARD)
  const [countdownLabel, setCountdownLabel] = useState('');
  const [countdownTotalSeconds, setCountdownTotalSeconds] = useState(null);
  const [currentStage, setCurrentStage] = useState({ name: 'TRADING SESSION', type: 'trading', desc: 'Markets Open' });
  const [nextEvent, setNextEvent] = useState(''); // What comes after current countdown

  // Trading session names by block (themed after global markets)
  const getTradingSessionName = (block) => {
    const sessions = {
      1: 'ASIAN SESSION',
      2: 'EUROPEAN SESSION',
      3: 'CANADIAN SESSION',
      4: 'POWER HOUR'
    };
    return sessions[block] || 'TRADING SESSION';
  };

  // Next macro game by trading block (matches Overview logic)
  const getNextGameForBlock = (block) => {
    const games = {
      1: 'Portfolio Warfare',
      2: 'Dead Cat Bounce',
      3: 'A Brief Case'
      // Block 4 has no macro game - goes to closing bell
    };
    return games[block] || (block >= 4 ? 'Closing Bell' : 'Next Phase');
  };

  // Broadcast state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastUrl, setBroadcastUrl] = useState(''); // Will be set by admin

  // Check if user is logged in
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!(userId && token));
  }, []);


  // Fetch market data
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        // Fetch party state first (source of truth)
        try {
          const partyStateRes = await fetch(`${API_BASE_URL}/api/party-state`);
          if (partyStateRes.ok) {
            const psData = await partyStateRes.json();
            const ps = psData.partyState;
            const block = ps?.tradingBlockNumber || 1;
            if (ps?.phase === 'trading') {
              setMarketStatus('OPEN');
              // Set current stage from trading block number
              setCurrentStage({
                name: getTradingSessionName(block),
                type: 'trading',
                desc: 'Markets Open'
              });
            } else if (ps?.phase === 'ended') {
              setMarketStatus('CLOSED');
              setCurrentStage({ name: 'MARKET CLOSE', type: 'ended', desc: 'Final Standings' });
            } else if (ps?.phase === 'macro_game') {
              setMarketStatus('HALTED');
              const gameName = ps.currentMacroGame || 'Macro Game';
              setCountdownLabel(gameName);
              setCurrentStage({
                name: gameName.toUpperCase(),
                type: 'game',
                desc: 'Team Competition'
              });
            } else if (ps?.phase === 'orientation') {
              setMarketStatus('SOON');
              setCurrentStage({ name: 'ORIENTATION', type: 'orientation', desc: 'Watch the Video' });
            } else {
              // Premarket, not_started, etc - show as "SOON"
              setMarketStatus('SOON');
              setCurrentStage({ name: 'PREMARKET', type: 'premarket', desc: 'Registration Open' });
            }
          } else {
            // Fallback to market status endpoint
            const statusResponse = await fetch(`${API_BASE_URL}/api/market/status`);
            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              setMarketStatus(statusData.status || 'OPEN');
            } else {
              setMarketStatus('SOON');
            }
          }
        } catch (statusError) {
          console.log('Party state not available, using fallback');
          setMarketStatus('SOON');
        }

        // Fetch stocks
        const stocksResponse = await fetch(`${API_BASE_URL}/api/stocks`);
        if (stocksResponse.ok) {
          const stocks = await stocksResponse.json();

          // Fetch market overview
          const overviewResponse = await fetch(`${API_BASE_URL}/api/market/overview`);
          let overview = null;
          if (overviewResponse.ok) {
            overview = await overviewResponse.json();
          }

          // Get most active stocks - use price change as activity metric since volume24h is 0
          let mostActive = [];
          if (overview?.mostActive && overview.mostActive.length > 0) {
            mostActive = overview.mostActive.map(stock => ({
              ...stock,
              // Calculate activity score based on price volatility
              activityScore: Math.abs((stock.currentPrice - stock.basePrice) / stock.basePrice) * 1000
            }));
          } else {
            // Fallback: sort stocks by price change (volatility = activity)
            mostActive = [...stocks].map(stock => ({
              ...stock,
              activityScore: Math.abs((stock.currentPrice - stock.basePrice) / stock.basePrice) * 1000
            })).sort((a, b) => b.activityScore - a.activityScore);
          }

          setMarketData({
            stocks: stocks, // All stocks for display
            marketStats: overview?.marketStats,
            topMovers: overview?.topMovers?.slice(0, 5) || [],
            mostActive: mostActive.slice(0, 5)
          });
        }

        // Fetch recent market events
        try {
          const eventsResponse = await fetch(`${API_BASE_URL}/api/events?limit=5`);
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            // The API returns an array directly, not wrapped in an object
            setMarketEvents(Array.isArray(eventsData) ? eventsData : (eventsData.events || []));
          }
        } catch (eventsError) {
          console.log('Could not fetch market events:', eventsError);
        }
      } catch (error) {
        console.error('Error fetching market data:', error);
        setMarketStatus('ERROR');
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch initial states on page load
  useEffect(() => {
    const fetchInitialStates = async () => {
      try {
        // Phases where we should NOT show premarket countdown (they show ACTIVE instead)
        const inProgressPhases = ['orientation', 'macro_game', 'power_hour', 'liquidity_crisis', 'winners', 'results'];

        // First check party state to know if we should fetch countdowns
        let currentPhase = 'not_started';
        const partyRes = await fetch(`${API_BASE_URL}/api/party-state`);
        if (partyRes.ok) {
          const data = await partyRes.json();
          if (data.partyState?.phase) {
            currentPhase = data.partyState.phase;
          }
        }

        // If we're in an "in progress" phase, don't fetch premarket countdown
        const skipPremarket = inProgressPhases.includes(currentPhase);

        // Fetch pre-market countdown (long countdown, ~60 min) - only if not in progress phase
        if (!skipPremarket) {
          const premarketRes = await fetch(`${API_BASE_URL}/api/premarket-countdown`);
          if (premarketRes.ok) {
            const data = await premarketRes.json();
            if (data.active && data.secondsRemaining > 0) {
              setPremarketCountdown(data.secondsRemaining);
              setCountdownTotalSeconds(data.totalSeconds);
            }
          }
        } else {
          // Clear any existing premarket countdown
          setPremarketCountdown(null);
        }

        // Fetch market open countdown (short countdown, 10-30 sec)
        const marketRes = await fetch(`${API_BASE_URL}/api/market-countdown`);
        if (marketRes.ok) {
          const data = await marketRes.json();
          if (data.active && data.secondsRemaining > 0) {
            setMarketOpenCountdown(data.secondsRemaining);
            setCountdownTotalSeconds(data.totalSeconds);
          }
        }

        // Fetch macro game countdown
        const macroRes = await fetch(`${API_BASE_URL}/api/macro-countdown`);
        if (macroRes.ok) {
          const data = await macroRes.json();
          if (data.active && data.secondsRemaining > 0) {
            setMacroGameCountdown(data.secondsRemaining);
            setCountdownTotalSeconds(data.totalSeconds);
            setCountdownLabel(data.gameName || 'Macro Game');
          }
        }

        // Fetch trading block countdown (SOFT - trading session timer)
        const tradingBlockRes = await fetch(`${API_BASE_URL}/api/trading-block-countdown`);
        if (tradingBlockRes.ok) {
          const data = await tradingBlockRes.json();
          if (data.active && data.secondsRemaining > 0) {
            setTradingBlockCountdown(data.secondsRemaining);
            setCountdownTotalSeconds(data.totalSeconds);
            // Derive next game from block number (matches Overview logic)
            setNextEvent(getNextGameForBlock(data.blockNumber));
          }
        }

        // Fetch broadcast URL
        const broadcastRes = await fetch(`${API_BASE_URL}/api/broadcast-url`);
        if (broadcastRes.ok) {
          const data = await broadcastRes.json();
          if (data.url) {
            setBroadcastUrl(data.url);
          }
        }

        // Party state is now fetched in the main fetchMarketData useEffect
      } catch (error) {
        console.log('Could not fetch initial states:', error);
      }
    };

    fetchInitialStates();
  }, []);

  // Socket connection for countdown updates from admin
  useEffect(() => {
    const socket = io(API_BASE_URL, { auth: { public: true } });

    // Registration phase - runs parallel with premarket
    socket.on('registration-start', (data) => {
      console.log('Registration phase started:', data);
      // Registration doesn't affect countdown - guests are registering while broadcast plays
    });

    socket.on('registration-end', () => {
      console.log('Registration phase ending');
    });

    // Pre-market countdown (long countdown) - runs parallel with registration
    socket.on('premarket-start', (data) => {
      if (data.minutes) {
        setPremarketCountdown(data.minutes * 60);
        setCountdownTotalSeconds(data.minutes * 60);
      }
    });

    socket.on('premarket-end', () => {
      setPremarketCountdown(null);
    });

    // Orientation events - pauses everything
    socket.on('orientation-start', () => {
      setPremarketCountdown(null); // Clear premarket countdown
      setMarketStatus('SOON');
      setCurrentStage({ name: 'ORIENTATION', type: 'orientation', desc: 'Watch the Video' });
    });

    socket.on('orientation-end', () => {
      setCurrentStage({ name: 'GET READY', type: 'pre-open', desc: 'Market Opening Soon' });
    });

    // Market open countdown (short countdown before bell)
    socket.on('countdown-start', (data) => {
      setPremarketCountdown(null); // Clear pre-market when short countdown starts
      setMarketOpenCountdown(data.seconds);
      setCountdownTotalSeconds(data.totalSeconds || data.seconds);
    });

    // Countdown modified (time added/subtracted)
    socket.on('countdown-update', (data) => {
      if (data.seconds !== undefined) {
        setMarketOpenCountdown(data.seconds);
      }
    });

    // Countdown cancelled by admin
    socket.on('countdown-cancelled', () => {
      setMarketOpenCountdown(null);
    });

    socket.on('market-bell', (data) => {
      if (data.type === 'OPENING_BELL') {
        setPremarketCountdown(null);
        setMarketOpenCountdown(null);
        setMarketStatus('OPEN');
      } else if (data.type === 'CLOSING_BELL') {
        setMarketStatus('CLOSED');
      }
    });

    // Macro game countdown
    socket.on('macro-game-countdown', (data) => {
      setMacroGameCountdown(data.seconds);
      setCountdownTotalSeconds(data.totalSeconds || data.seconds);
      setCountdownLabel(data.gameName || 'Macro Game');
    });

    socket.on('macro-game-countdown-end', () => {
      setMacroGameCountdown(null);
      setCountdownLabel('');
    });

    // Trading block countdown (SOFT - trading session timer)
    socket.on('trading-block-countdown-start', (data) => {
      setTradingBlockCountdown(data.seconds);
      setCountdownTotalSeconds(data.totalSeconds || data.seconds);
      // Derive next game from block number (matches Overview logic)
      setNextEvent(getNextGameForBlock(data.blockNumber));
      // Update current stage based on block
      const sessionName = data.sessionName || getTradingSessionName(data.blockNumber);
      setCurrentStage({
        name: sessionName,
        type: 'trading',
        desc: 'Markets Open'
      });
    });

    socket.on('trading-block-countdown-clear', () => {
      setTradingBlockCountdown(null);
    });

    // Closing bell countdown (HARD - auto-closes market when done)
    socket.on('close-countdown-start', (data) => {
      setCloseCountdown(data.seconds);
      setCountdownTotalSeconds(data.totalSeconds || data.seconds);
      setTradingBlockCountdown(null); // Clear soft countdown when hard starts
    });

    // Broadcast URL update
    socket.on('broadcast-url', (data) => {
      setBroadcastUrl(data.url || '');
    });

    // Party state update (source of truth from server)
    socket.on('party-state-update', (data) => {
      if (data.partyState) {
        const block = data.partyState.tradingBlockNumber || 1;

        // Update market status and stage based on phase
        if (data.partyState.phase === 'trading') {
          setMarketStatus('OPEN');
          setCurrentStage({
            name: getTradingSessionName(block),
            type: 'trading',
            desc: 'Markets Open'
          });
        } else if (data.partyState.phase === 'ended') {
          setMarketStatus('CLOSED');
          setCurrentStage({ name: 'MARKET CLOSE', type: 'ended', desc: 'Final Standings' });
        } else if (data.partyState.phase === 'macro_game') {
          // During macro game, market is paused
          setMarketStatus('HALTED');
          setCountdownLabel(data.partyState.currentMacroGame || 'Macro Game');
          setCurrentStage({
            name: data.partyState.currentMacroGame || 'MACRO GAME',
            type: 'macro_game',
            desc: 'Trading Halted'
          });
        } else if (data.partyState.phase === 'orientation') {
          setMarketStatus('SOON');
          setCurrentStage({ name: 'ORIENTATION', type: 'orientation', desc: 'Watch the Video' });
        } else if (data.partyState.phase === 'premarket' || data.partyState.phase === 'registration') {
          setMarketStatus('SOON');
          setCurrentStage({ name: 'PREMARKET', type: 'premarket', desc: 'Registration Open' });
        }
      }
    });

    // Trading block update (for showing next game info)
    socket.on('trading-block-update', (data) => {
      // Could display trading block info or next game preview
      console.log('Trading block update:', data);
    });

    return () => socket.disconnect();
  }, []);

  // Countdown timer effect - ticks down active countdowns
  useEffect(() => {
    const hasActiveCountdown = (premarketCountdown !== null && premarketCountdown > 0) ||
                               (marketOpenCountdown !== null && marketOpenCountdown > 0) ||
                               (macroGameCountdown !== null && macroGameCountdown > 0) ||
                               (tradingBlockCountdown !== null && tradingBlockCountdown > 0) ||
                               (closeCountdown !== null && closeCountdown > 0);
    if (!hasActiveCountdown) return;

    const timer = setInterval(() => {
      if (premarketCountdown !== null && premarketCountdown > 0) {
        setPremarketCountdown(prev => prev !== null && prev > 0 ? prev - 1 : null);
      }
      if (marketOpenCountdown !== null && marketOpenCountdown > 0) {
        setMarketOpenCountdown(prev => prev !== null && prev > 0 ? prev - 1 : null);
      }
      if (macroGameCountdown !== null && macroGameCountdown > 0) {
        setMacroGameCountdown(prev => prev !== null && prev > 0 ? prev - 1 : null);
      }
      if (tradingBlockCountdown !== null && tradingBlockCountdown > 0) {
        setTradingBlockCountdown(prev => prev !== null && prev > 0 ? prev - 1 : null);
      }
      if (closeCountdown !== null && closeCountdown > 0) {
        setCloseCountdown(prev => prev !== null && prev > 0 ? prev - 1 : null);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [premarketCountdown, marketOpenCountdown, macroGameCountdown, tradingBlockCountdown, closeCountdown]);

  // Format pre-market countdown as MM:SS (same as other countdowns)
  const formatPremarketCountdown = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format countdown time
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate market change percentage
  const calculateMarketChange = () => {
    if (!marketData.stocks.length) return 0;
    const totalChange = marketData.stocks.reduce((sum, stock) => {
      const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;
      return sum + change;
    }, 0);
    return totalChange / marketData.stocks.length;
  };

  const marketChange = calculateMarketChange();

  // HowItWorksModal content - rendered inline to prevent scroll reset on parent re-renders

  return (
    <div className="trading-terminal home-page-wrapper">
      {/* Live Stock Ticker Tape */}
      <LiveTickerTape
        speed="medium"
        height="50px"
        showVolume={false}
        showMarketStatus={false}
      />

      {/* Main Trading Dashboard */}
      <div className="trading-dashboard">

        {/* Left Panel - Market Data */}
        <div className="left-panel">
          {/* Countdown Widget - shows pre-market, market open, or macro game countdown */}
          <div className="widget countdown-widget">
            <h3 className="widget-title">CURRENT STAGE</h3>
            <div className="countdown-display">
              {/* Always show current stage */}
              <div className={`countdown-time ${
                marketStatus === 'OPEN' ? 'countdown-trading' :
                marketStatus === 'HALTED' ? 'countdown-halted' :
                marketStatus === 'CLOSED' ? 'countdown-closed' : 'countdown-premarket'
              }`}>
                {marketStatus === 'CLOSED' ? 'CLOSED' : currentStage.name}
              </div>
              <div className="countdown-label">
                {marketStatus === 'OPEN' ? 'Markets Open' :
                 marketStatus === 'HALTED' ? 'Markets Halted' :
                 marketStatus === 'CLOSED' ? 'Final Standings' :
                 currentStage.desc}
              </div>
            </div>

            {/* Show countdown if active - Priority: HARD countdowns first, then SOFT */}
            {(() => {
              // HARD countdowns (auto-trigger) take priority
              const hasHardCountdown = closeCountdown > 0 || marketOpenCountdown > 0 || macroGameCountdown > 0;
              // SOFT countdowns (visual guide only)
              const hasSoftCountdown = tradingBlockCountdown > 0 || premarketCountdown > 0;
              // Market is OPEN but no countdown running - show "SOON" (waiting for next phase)
              const showSoon = marketStatus === 'OPEN' && !hasHardCountdown && !hasSoftCountdown && nextEvent;

              if (hasHardCountdown || hasSoftCountdown || showSoon) {
                return (
                  <div className="countdown-timer-section">
                    <div className="countdown-timer-display">
                      {/* HARD: Closing Bell */}
                      {closeCountdown > 0 ? (
                        <>
                          <div className="countdown-timer-value countdown-hard">{formatCountdown(closeCountdown)}</div>
                          <div className="countdown-timer-label">until Closing Bell</div>
                        </>
                      ) : /* HARD: Opening Bell */
                      marketOpenCountdown > 0 ? (
                        <>
                          <div className="countdown-timer-value countdown-hard">{formatCountdown(marketOpenCountdown)}</div>
                          <div className="countdown-timer-label">until Opening Bell</div>
                        </>
                      ) : /* HARD: Macro Game */
                      macroGameCountdown > 0 ? (
                        <>
                          <div className="countdown-timer-value countdown-hard">{formatCountdown(macroGameCountdown)}</div>
                          <div className="countdown-timer-label">until {countdownLabel || 'Macro Game'}</div>
                        </>
                      ) : /* SOFT: Trading Session */
                      tradingBlockCountdown > 0 ? (
                        <>
                          <div className="countdown-timer-value countdown-soft">{formatCountdown(tradingBlockCountdown)}</div>
                          <div className="countdown-timer-label">next: {nextEvent || 'Macro Game'}</div>
                        </>
                      ) : /* SOFT: Premarket */
                      premarketCountdown > 0 ? (
                        <>
                          <div className="countdown-timer-value countdown-soft">{formatPremarketCountdown(premarketCountdown)}</div>
                          <div className="countdown-timer-label">until Market Open</div>
                        </>
                      ) : /* Market open but no countdown - show SOON */
                      showSoon ? (
                        <>
                          <div className="countdown-timer-value countdown-soon">SOON</div>
                          <div className="countdown-timer-label">waiting for {nextEvent || 'next phase'}</div>
                        </>
                      ) : null}
                    </div>
                    {/* Progress bar for active countdowns */}
                    {(closeCountdown > 0 || marketOpenCountdown > 0 || macroGameCountdown > 0 || tradingBlockCountdown > 0 || premarketCountdown > 0) && (
                      <div className="countdown-progress">
                        <div
                          className={`countdown-bar ${
                            closeCountdown > 0 ? 'countdown-bar-close' :
                            marketOpenCountdown > 0 ? 'countdown-bar-market' :
                            macroGameCountdown > 0 ? 'countdown-bar-game' :
                            tradingBlockCountdown > 0 ? 'countdown-bar-trading' :
                            'countdown-bar-premarket'
                          }`}
                          style={{
                            width: `${((closeCountdown || marketOpenCountdown || macroGameCountdown || tradingBlockCountdown || premarketCountdown) /
                              (countdownTotalSeconds || 1)) * 100}%`
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Market Statistics */}
          {/* <div className="widget market-stats-widget">
            <h3 className="widget-title">MARKET OVERVIEW</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Total Stocks</label>
                <span>{marketData.marketStats?.totalStocks || marketData.stocks.length}</span>
              </div>
              <div className="stat-item">
                <label>24h Volume</label>
                <span>{(marketData.marketStats?.totalVolume24h || 0).toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <label>24h Trades</label>
                <span>{marketData.marketStats?.totalTrades24h || 0}</span>
              </div>
              <div className="stat-item">
                <label>Avg Change</label>
                <span className={marketChange >= 0 ? 'positive' : 'negative'}>
                  {marketChange >= 0 ? '+' : ''}{marketChange.toFixed(2)}%
                </span>
              </div>
            </div>
          </div> */}

          {/* Top Movers */}
          <div className="widget movers-widget">
            <h3 className="widget-title">TOP MOVERS</h3>
            <div className="movers-list">
              {(marketData.topMovers.length > 0 ? marketData.topMovers : marketData.stocks).slice(0, 5).map((stock, index) => {
                const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : (stock.changePercent || 0);
                return (
                  <div key={stock.id || index} className="mover-item">
                    <Link to={`/stock/${stock.ticker}`} className="stock-link">
                      <span className="ticker">{stock.ticker}</span>
                      <span className="price">{formatCurrency(stock.currentPrice)}</span>
                      <span className={`change ${change >= 0 ? 'positive' : 'negative'}`}>
                        {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                      </span>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Access */}
          <div className="widget quick-access-widget">
            <h3 className="widget-title">QUICK ACCESS</h3>
            <div className="quick-buttons">
              {!isLoggedIn ? (
                <>
                  <Link to="/register" className="quick-btn primary">
                    🎯 START TRADING
                  </Link>
                  <Link to="/login" className="quick-btn secondary">
                    👤 LOGIN
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/market" className="quick-btn primary">
                    📊 MARKET
                  </Link>
                  <Link to="/portfolio" className="quick-btn secondary">
                    💼 PORTFOLIO
                  </Link>
                </>
              )}
              <Link to="/leaderboard" className="quick-btn">
                🏆 RANKINGS
              </Link>
              <Link to="/scan" className="quick-btn">
                📱 SCAN BONUS
              </Link>
              <Link to="/faq" className="quick-btn info">
                ❓ HOW TO PLAY
              </Link>
            </div>
          </div>
        </div>

        {/* Center Panel - Main Display */}
        <div className="center-panel">
          {/* Hero Trading Section */}
          <div className="trading-hero">
            <div className="hero-content">
              {/* Market Status Badge */}
              <div className={`market-status-badge ${marketStatus.toLowerCase()}`}>
                <span className="status-dot">●</span>
                <span className="status-text">
                  {marketStatus === 'HALTED' ? 'TRADING HALTED' :
                   marketStatus === 'SOON' ? 'MARKET OPENING SOON' :
                   `MARKET ${marketStatus}`}
                </span>
              </div>

              <h1 className="trading-title">
                <span className="company">STOCK MARKET</span>
                <span className="product">PARTY</span>
              </h1>
              <p className="trading-subtitle">
                KOMOK
              </p>
              <div className="market-metrics">
                <div className="metric">
                  <span className="metric-value">{marketData.stocks.length}</span>
                  <span className="metric-label">ACTIVE STOCKS</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{(marketData.marketStats?.totalTrades24h || 0).toLocaleString()}</span>
                  <span className="metric-label">24H TRADES</span>
                </div>
                <div className="metric">
                  <span className={`metric-value ${marketChange >= 0 ? 'positive' : 'negative'}`}>
                    {marketChange >= 0 ? '+' : ''}{marketChange.toFixed(1)}%
                  </span>
                  <span className="metric-label">MARKET CHANGE</span>
                </div>
              </div>
            </div>
          </div>

          {/* All Stocks Grid */}
          <div className="stocks-grid stock-grid-flex">
            <h3 className="section-title">ALL STOCKS ({marketData.stocks.length})</h3>
            <div className="stocks-container">
              {marketData.stocks.map((stock, index) => {
                const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;
                return (
                  <Link key={stock.id || index} to={`/stock/${stock.ticker}`} className="stock-card">
                    <div className="stock-header-row">
                      <span className="stock-ticker-large">{stock.ticker}</span>
                      <span className="stock-name-large">{stock.name}</span>
                    </div>
                    <div className="stock-chart-placeholder">
                      {/* Mini chart from actual price history with color variation */}
                      <div className="mini-chart">
                        {stock.history && stock.history.length > 0 ? (
                          // Use last 8 price points from history
                          stock.history.slice(-8).map((point, i, arr) => {
                            const historyPrices = stock.history.slice(-8).map(p => parseFloat(p.price));
                            const minPrice = Math.min(...historyPrices);
                            const maxPrice = Math.max(...historyPrices);
                            const range = maxPrice - minPrice || 1;
                            const normalizedHeight = ((parseFloat(point.price) - minPrice) / range) * 60 + 20;

                            // Determine bar color based on price change from previous bar
                            let barColor;
                            if (i === 0) {
                              barColor = change >= 0 ? '#00ff41' : '#ff4444';
                            } else {
                              const prevPrice = parseFloat(arr[i - 1].price);
                              const currPrice = parseFloat(point.price);
                              if (currPrice > prevPrice) {
                                barColor = '#00ff41'; // Green for up
                              } else if (currPrice < prevPrice) {
                                barColor = '#ff4444'; // Red for down
                              } else {
                                barColor = 'rgba(0, 255, 65, 0.5)'; // Dim green for flat
                              }
                            }

                            return (
                              <div
                                key={i}
                                className="chart-bar"
                                style={{
                                  height: `${normalizedHeight}%`,
                                  backgroundColor: barColor,
                                  opacity: 0.8 + (i * 0.025) // Fade in effect
                                }}
                              />
                            );
                          })
                        ) : (
                          // Fallback if no history
                          [...Array(8)].map((_, i) => (
                            <div
                              key={i}
                              className="chart-bar"
                              style={{
                                height: '50%',
                                backgroundColor: change >= 0 ? '#00ff41' : '#ff4444',
                                opacity: 0.7
                              }}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - Additional Info */}
        <div className="right-panel">
          {/* Most Active */}
          <div className="widget active-widget">
            <h3 className="widget-title">MOST ACTIVE</h3>
            <div className="active-list">
              {marketData.mostActive.length > 0 ? marketData.mostActive.map((stock, index) => {
                const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;
                return (
                  <div key={stock.id || index} className="active-item">
                    <Link to={`/stock/${stock.ticker}`} className="stock-link">
                      <span className="ticker">{stock.ticker}</span>
                      <span className={`volume ${change >= 0 ? 'positive' : 'negative'}`}>
                        {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
                      </span>
                    </Link>
                  </div>
                );
              }) : (
                <div className="no-data">Loading...</div>
              )}
            </div>
          </div>

          {/* Party Resources */}
          <div className="widget features-widget">
            <h3 className="widget-title">PARTY RESOURCES</h3>
            <div className="features-list">
              <Link to="/videos" className="feature-item">
                <span className="feature-icon">🎬</span>
                <div className="feature-info">
                  <span className="feature-name">Orientation</span>
                  <span className="feature-desc">Watch video</span>
                </div>
              </Link>
              <Link to="/broadcast" className="feature-item">
                <span className="feature-icon">📡</span>
                <div className="feature-info">
                  <span className="feature-name">Live Broadcast</span>
                  <span className="feature-desc">{broadcastUrl ? 'Watch now' : 'Watch now'}</span>
                </div>
              </Link>
              <Link to="/market" className="feature-item">
                <span className="feature-icon">📊</span>
                <div className="feature-info">
                  <span className="feature-name">Trade Now</span>
                  <span className="feature-desc">Buy & sell stocks</span>
                </div>
              </Link>
              <Link to="/faq" className="feature-item">
                <span className="feature-icon">📖</span>
                <div className="feature-info">
                  <span className="feature-name">Trading Rules</span>
                  <span className="feature-desc">FAQ & tips</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Live Broadcast Mini Preview */}
          {broadcastUrl && (
            <div className="widget broadcast-widget">
              <h3 className="widget-title">
                <span className="live-indicator">●</span> LIVE BROADCAST
              </h3>
              <div className="broadcast-preview" onClick={() => setShowBroadcastModal(true)}>
                <iframe
                  src={broadcastUrl}
                  title="Live Broadcast"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                <div className="broadcast-expand-hint">Tap to expand</div>
              </div>
            </div>
          )}

          {/* Market News - Real Events */}
          <div className="widget news-widget">
            <Link to="/events" className="widget-title widget-title-link">RECENT EVENTS</Link>
            <div className="news-list">
              {marketEvents.length > 0 ? (
                marketEvents.map((event, index) => {
                  const eventTime = new Date(event.timestamp || event.created_at);
                  // Get affected stocks - handle both array and string formats
                  const rawAffected = event.affectedStocks;
                  const isMarketWide = rawAffected === 'ALL' || rawAffected === 'all';
                  const affectedStocks = Array.isArray(rawAffected) ? rawAffected : [];
                  const stockTickers = affectedStocks
                    .filter(s => s !== 'ALL' && s !== 'all')
                    .map(stockId => {
                      if (typeof stockId === 'string' && !stockId.startsWith('stock-')) {
                        return stockId; // Already a ticker
                      }
                      const stock = marketData.stocks.find(s => s.id === stockId);
                      return stock?.ticker;
                    })
                    .filter(Boolean)
                    .slice(0, 3);
                  const stockLabel = stockTickers.length > 0 ? ` [${stockTickers.join(', ')}]` :
                                     isMarketWide ? ' [ALL]' : '';
                  return (
                    <div key={event.id || index} className="news-item">
                      <span className="news-time">
                        {eventTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                      <span className="news-text">
                        {event.description || event.message || event.type}
                        {stockLabel && <span className="news-stocks">{stockLabel}</span>}
                      </span>
                    </div>
                  );
                })
              ) : (
                <>
                  <div className="news-item">
                    <span className="news-time">{currentTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    <span className="news-text">
                      {marketStatus === 'HALTED' ? 'Trading halted for macro game' :
                       marketStatus === 'SOON' ? 'Market opening soon' :
                       marketStatus === 'OPEN' ? 'Market open for live trading' :
                       'Market closed'}
                    </span>
                  </div>
                  <div className="news-item">
                    <span className="news-time">{new Date(currentTime.getTime() - 300000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    <span className="news-text">Real-time price updates active</span>
                  </div>
                  <div className="news-item">
                    <span className="news-time">{new Date(currentTime.getTime() - 600000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    <span className="news-text">Trading platform online</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Login CTA if not logged in */}
          {!isLoggedIn && (
            <div className="widget login-cta-widget">
              <h3 className="widget-title">GET STARTED</h3>
              <p className="cta-text">Join the party and start trading now!</p>
              <Link to="/register" className="cta-button primary">
                🎯 Register Now
              </Link>
              <Link to="/login" className="cta-button secondary">
                👤 Login
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Ticker Tape */}
      <LiveTickerTape
        speed="slow"
        height="45px"
        showVolume={true}
        showMarketStatus={false}
      />

      {/* Broadcast Modal */}
      {showBroadcastModal && broadcastUrl && (
        <div className="broadcast-modal" onClick={() => setShowBroadcastModal(false)}>
          <div className="broadcast-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="broadcast-close" onClick={() => setShowBroadcastModal(false)}>×</button>
            <h2><span className="live-indicator">●</span> Live Broadcast</h2>
            <div className="broadcast-video-container">
              <iframe
                src={broadcastUrl}
                title="Live Broadcast"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
